/**
 * Custody of the `beforeinstallprompt` event.
 *
 * Chromium fires it once, early — routinely before React has mounted — and the
 * event is single-use: `prompt()` can only be called on the live object, so it has
 * to be caught at module scope and kept. `initInstallPrompt()` is called from
 * main.tsx for exactly that reason; the React side reads it back through
 * hooks/useInstallPrompt.ts.
 *
 * Not available on iOS at all (Safari has no install prompt API), which is why the
 * install CTA is a Chromium-only affordance.
 */
import { markInstalled } from './pwa-installed';

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function notify() {
  for (const l of listeners) l();
}

/** Call once, as early as possible. Idempotent. */
export function initInstallPrompt(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('beforeinstallprompt', (e) => {
    // Without preventDefault Chrome shows its own mini-infobar and never hands us
    // the event to re-fire later at the moment that actually converts (right after
    // a friend's link landed them here).
    e.preventDefault();
    deferred = e;
    notify();
  });

  window.addEventListener('appinstalled', () => {
    deferred = null;
    // L'unico momento in cui l'installazione e' un fatto osservato e non una
    // deduzione: da qui in poi la card della dashboard non ha piu' niente da dire.
    markInstalled();
    notify();
  });
}

export function canInstall(): boolean {
  return deferred !== null;
}

export function subscribeInstallPrompt(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Show the browser's install dialog. The event is spent either way — Chrome will
 * not let the same one be re-prompted — so it is dropped regardless of the outcome.
 *
 * @returns whether the user accepted.
 */
export async function showInstallPrompt(): Promise<boolean> {
  const event = deferred;
  if (!event) return false;
  deferred = null;
  notify();
  try {
    await event.prompt();
    const { outcome } = await event.userChoice;
    return outcome === 'accepted';
  } catch {
    return false;
  }
}
